// Betrieb › Radio › „Umzug & Export“ (h-umzug).
//
// Ein Radio vollständig als Archiv exportieren – zum Umzug auf einen anderen
// Server oder als Sicherung vor dem Löschen. Das Archiv enthält Geheimnisse
// (Passwort-Hashes, Sende-Passwörter); darum der deutliche Hinweis.

import { useState } from "react";
import { Link } from "react-router-dom";
import { umzugApi } from "../../api";
import { errorText } from "../../components/streaming-ui";
import { Sym } from "../../components/social-ui";
import { Button, Callout, Card } from "../../components/ui";
import { ExportListe, laeuftNoch, useAuftraege } from "../../components/umzug-ui";

export function UmzugKarte({ radioId, radioName }: { radioId: string; radioName: string }): JSX.Element {
  const [mitschnitte, setMitschnitte] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const { liste, neu } = useAuftraege(() => umzugApi.exporte(radioId).then((r) => r.exporte));

  const starten = async (): Promise<void> => {
    setLaeuft(true);
    setFehler(null);
    try {
      await umzugApi.exportStarten(radioId, mitschnitte);
      neu();
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setLaeuft(false);
    }
  };
  const aktiv = !!liste?.some(laeuftNoch);

  return (
    <Card
      title="Umzug & Export"
      description={`„${radioName}“ vollständig als Archiv: Datenbank, Mediathek, Bilder – auf demselben oder einem anderen Server wieder importierbar.`}
      className="mb-5"
    >
      <Callout tone="warning" title="Das Archiv ist sensibel" className="mb-4">
        Es enthält Passwort-Hashes, Zwei-Faktor-Schlüssel und Sende-Passwörter. Nur verschlüsselt aufbewahren oder übertragen
        und nach dem Umzug löschen. Auf dem Server liegt es höchstens 7 Tage.
      </Callout>
      {fehler && <Callout tone="danger" className="mb-4">{fehler}</Callout>}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <label className="flex items-start gap-2.5 text-sm">
          <input type="checkbox" className="mt-0.5 h-4 w-4" checked={mitschnitte} onChange={(e) => setMitschnitte(e.target.checked)} />
          <span>
            <b className="font-medium text-primary">Mitschnitte einschließen</b>
            <span className="block text-xs text-muted">Kann das Archiv sehr groß machen. Ohne Haken bleiben Mitschnitte draußen.</span>
          </span>
        </label>
        <Button variant="primary" disabled={laeuft || aktiv} onClick={() => void starten()} className="self-start sm:self-auto">
          <Sym name="download" /> {aktiv ? "Export läuft …" : "Exportieren"}
        </Button>
      </div>
      <div className="mt-5">
        <div className="section-label mb-2">Exporte dieses Radios</div>
        {liste === null ? (
          <p className="text-sm text-muted">Wird geladen …</p>
        ) : (
          <ExportListe
            exporte={liste}
            leer="Noch kein Export."
            onDownload={(a) => { umzugApi.exportHerunterladen(a.id).catch((err) => setFehler(errorText(err))); }}
            onLoeschen={(a) => { umzugApi.exportLoeschen(a.id).then(neu).catch((err) => setFehler(errorText(err))); }}
          />
        )}
      </div>
      <p className="mt-4 text-sm text-muted">
        Umzug auf diesen Server? <Link to="/betrieb/import" className="font-medium text-brand-primary hover:underline">Radio aus einem Archiv importieren</Link>
      </p>
    </Card>
  );
}
